/**
 * Follow-up queue above the Work composer — only while something is queued.
 *
 * Queued messages wait until the current task finishes, then send as the
 * next turn. Enter queues; Send now interrupts the live run. The list is
 * height-capped (scroll + bottom fade) on every layout so it cannot eat
 * the transcript. Drag the handle or use move up/down to reorder. An empty
 * queue does not reserve a box above the composer.
 */
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View, Pressable, StyleSheet, Platform, TextInput, ScrollView } from 'react-native';
import PropTypes from 'prop-types';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/context/ThemeContext';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import { Tooltip } from '@/components/Tooltip';
import { useFileStore } from '@/store';
import { workFollowUpQueueCountLabel } from '@/constants/work_followup_queue';
import useResponsiveness from '@/hooks/useResponsiveness';

const EMPTY_FOLLOWUPS = [];
const ROW_HEIGHT = 36;
const ROW_HEIGHT_DESKTOP = 38;
const MAX_VISIBLE_MOBILE = 3;
const MAX_VISIBLE_DESKTOP = 4;

function QueueIconButton({
  name,
  label,
  onPress,
  color,
  destructive,
  disabled = false,
  compact = false,
}) {
  const { colors } = useTheme();
  const iconColor = destructive ? colors.color_destructive : (color || colors.text_color_secondary);
  const size = compact ? 24 : 28;
  return (
    <Tooltip label={label} disabled={disabled}>
      <Pressable
        onPress={onPress}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={label}
        hitSlop={{ top: 4, bottom: 4, left: 4, right: 4 }}
        style={({ hovered, pressed }) => [
          styles.iconBtn,
          { width: size, height: size, borderRadius: compact ? 6 : 8 },
          {
            backgroundColor: hovered || pressed
              ? (destructive ? `${colors.color_destructive}18` : colors.solid_color_secondary)
              : 'transparent',
          },
          disabled && { opacity: 0.28 },
        ]}
      >
        <Icon name={name} size={compact ? 11 : 12} color={iconColor} />
      </Pressable>
    </Tooltip>
  );
}

function MoveButtons({ index, count, compact, onMove }) {
  const { colors } = useTheme();
  return (
    <View style={styles.moveCol}>
      <Pressable
        onPress={() => onMove(index, index - 1)}
        disabled={index === 0}
        accessibilityRole="button"
        accessibilityLabel="Move up"
        hitSlop={{ top: 2, bottom: 0, left: 4, right: 4 }}
        style={({ hovered, pressed }) => [
          styles.moveBtn,
          (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
          index === 0 && { opacity: 0.28 },
        ]}
      >
        <Icon name="chevron-up" size={compact ? 9 : 10} color={colors.text_color_tertiary} />
      </Pressable>
      <Pressable
        onPress={() => onMove(index, index + 1)}
        disabled={index === count - 1}
        accessibilityRole="button"
        accessibilityLabel="Move down"
        hitSlop={{ top: 0, bottom: 2, left: 4, right: 4 }}
        style={({ hovered, pressed }) => [
          styles.moveBtn,
          (hovered || pressed) && { backgroundColor: colors.solid_color_secondary },
          index === count - 1 && { opacity: 0.28 },
        ]}
      >
        <Icon name="chevron-down" size={compact ? 9 : 10} color={colors.text_color_tertiary} />
      </Pressable>
    </View>
  );
}

function FollowUpRow({
  item,
  index,
  count,
  fileNames,
  sendingId,
  compact = false,
  dragging = false,
  dropTarget = false,
  onEdit,
  onDelete,
  onSendNow,
  onMove,
  onDragStart,
  onDragMove,
  onDragEnd,
  bindRowRef,
}) {
  const { colors } = useTheme();
  const [draft, setDraft] = useState(item.text);
  const [editing, setEditing] = useState(false);
  const busy = sendingId === item.id;
  const attachmentLabel = fileNames.length
    ? `${fileNames.length} ${fileNames.length === 1 ? 'file' : 'files'}`
    : null;
  const canReorder = count > 1 && !!onMove;

  const save = useCallback(() => {
    const next = String(draft || '').trim();
    if (!next && !item.fileIds?.length) return;
    onEdit(item.id, { text: next });
    setEditing(false);
  }, [draft, item.fileIds, item.id, onEdit]);

  if (editing) {
    return (
      <View
        ref={bindRowRef}
        style={[
          styles.editRow,
          { borderColor: colors.border_color_secondary, backgroundColor: colors.solid_color_primary },
        ]}
      >
        <TextInput
          value={draft}
          onChangeText={setDraft}
          multiline
          autoFocus
          accessibilityLabel="Edit follow-up"
          style={[styles.editInput, { color: colors.text_color_primary }]}
          placeholder="Follow-up message"
          placeholderTextColor={colors.text_color_tertiary}
        />
        <View style={styles.itemActions}>
          <Pressable
            onPress={() => { setDraft(item.text); setEditing(false); }}
            style={({ hovered }) => [styles.textBtn, hovered && { opacity: 0.8 }]}
            accessibilityRole="button"
            accessibilityLabel="Cancel editing"
          >
            <Text size="xxs" color="secondary">Cancel</Text>
          </Pressable>
          <Pressable
            onPress={save}
            style={({ hovered, pressed }) => [
              styles.saveBtn,
              { backgroundColor: colors.solid_color_toast },
              (hovered || pressed) && { opacity: 0.88 },
            ]}
            accessibilityRole="button"
            accessibilityLabel="Save follow-up"
          >
            <Text size="xxs" weight="semibold" style={{ color: colors.text_color_alt }}>Save</Text>
          </Pressable>
        </View>
      </View>
    );
  }

  const webDrag = Platform.OS === 'web' && canReorder ? {
    onPointerDown: (event) => onDragStart?.(item.id, event),
    onPointerMove: onDragMove,
    onPointerUp: onDragEnd,
    onPointerCancel: onDragEnd,
  } : {};

  return (
    <View
      ref={bindRowRef}
      style={[
        styles.row,
        compact ? styles.rowCompact : styles.rowDesktop,
        {
          borderBottomColor: colors.border_color_secondary,
        },
        dropTarget && { borderTopColor: colors.text_color_primary, borderTopWidth: 2 },
        index === count - 1 && { borderBottomWidth: 0 },
        dragging && { opacity: 0.45 },
      ]}
    >
      {canReorder ? (
        <>
          <View
            accessibilityRole="button"
            accessibilityLabel="Drag to reorder"
            {...webDrag}
            style={[
              styles.grip,
              Platform.OS === 'web' ? {
                cursor: dragging ? 'grabbing' : 'grab',
                touchAction: 'none',
                userSelect: 'none',
              } : null,
            ]}
          >
            <Icon name="bars" size={11} color={colors.text_color_tertiary} />
          </View>
          <MoveButtons index={index} count={count} compact={compact} onMove={onMove} />
        </>
      ) : null}
      <View style={styles.itemCopy}>
        <Text size={compact ? 'xs' : 's'} numberOfLines={1}>
          {item.text || 'Attached files'}
          {attachmentLabel ? ` · ${attachmentLabel}` : ''}
        </Text>
      </View>
      {compact ? (
        <QueueIconButton
          name="paper-plane"
          label={busy ? 'Sending…' : 'Send now'}
          onPress={() => onSendNow(item)}
          disabled={busy}
          compact
        />
      ) : (
        <Pressable
          onPress={() => onSendNow(item)}
          disabled={busy}
          accessibilityRole="button"
          accessibilityLabel="Send now"
          style={({ hovered, pressed }) => [
            styles.sendNowChip,
            {
              borderColor: colors.border_color_primary,
              backgroundColor: hovered || pressed ? colors.solid_color_secondary : 'transparent',
            },
            busy && { opacity: 0.5 },
          ]}
        >
          <Text size="xxs" weight="semibold">{busy ? 'Sending…' : 'Send now'}</Text>
        </Pressable>
      )}
      <QueueIconButton name="pen" label="Edit" compact={compact} onPress={() => { setDraft(item.text); setEditing(true); }} />
      <QueueIconButton name="trash" label="Delete" compact={compact} destructive onPress={() => onDelete(item.id)} />
    </View>
  );
}

function dropIndexFromY(clientY, ids, nodeMap) {
  let toIndex = ids.length - 1;
  for (let i = 0; i < ids.length; i += 1) {
    const node = nodeMap[ids[i]];
    if (!node || typeof node.getBoundingClientRect !== 'function') continue;
    const rect = node.getBoundingClientRect();
    if (clientY < rect.top + rect.height / 2) {
      toIndex = i;
      break;
    }
  }
  return toIndex;
}

export default function WorkFollowUpQueue({
  isActive = false,
  items = EMPTY_FOLLOWUPS,
  onEdit,
  onDelete,
  onReorder,
  onSendNow,
  onClear,
}) {
  const { colors } = useTheme();
  const { isXS } = useResponsiveness();
  const filesById = useFileStore((s) => s.files);
  const [sendingId, setSendingId] = useState(null);
  const [draggingId, setDraggingId] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);
  const [canScroll, setCanScroll] = useState(false);
  const [atEnd, setAtEnd] = useState(true);
  const draggingIdRef = useRef(null);
  const dropIndexRef = useRef(null);
  const itemsRef = useRef(items);
  const rowNodesRef = useRef({});
  const scrollMetricsRef = useRef({ contentH: 0, layoutH: 0, offsetY: 0 });
  itemsRef.current = items;

  const namedItems = useMemo(() => items.map((item) => ({
    item,
    fileNames: (item.fileIds || []).flatMap((id) => {
      const name = filesById?.[id]?.name || filesById?.[id]?.fileName;
      return name ? [name] : [];
    }),
  })), [items, filesById]);

  const sendNow = useCallback(async (item) => {
    if (!item || sendingId) return;
    setSendingId(item.id);
    try {
      await onSendNow?.(item);
    } finally {
      setSendingId(null);
    }
  }, [onSendNow, sendingId]);

  const moveTo = useCallback((fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    onReorder?.(fromIndex, toIndex);
  }, [onReorder]);

  const finishDrag = useCallback(() => {
    const id = draggingIdRef.current;
    const to = dropIndexRef.current;
    draggingIdRef.current = null;
    dropIndexRef.current = null;
    setDraggingId(null);
    setDropIndex(null);
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    if (!id || to == null) return;
    const from = itemsRef.current.findIndex((entry) => entry.id === id);
    if (from < 0 || from === to) return;
    onReorder?.(from, to);
  }, [onReorder]);

  const startDrag = useCallback((id, event) => {
    if (Platform.OS !== 'web') return;
    event.preventDefault?.();
    event.stopPropagation?.();
    const from = itemsRef.current.findIndex((entry) => entry.id === id);
    draggingIdRef.current = id;
    dropIndexRef.current = from;
    setDraggingId(id);
    setDropIndex(from);
    event.currentTarget?.setPointerCapture?.(event.pointerId);
    if (typeof document !== 'undefined') {
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
    }
  }, []);

  const moveDrag = useCallback((event) => {
    if (!draggingIdRef.current) return;
    const y = typeof event?.clientY === 'number' ? event.clientY : event?.nativeEvent?.clientY;
    if (!Number.isFinite(y)) return;
    const ids = itemsRef.current.map((entry) => entry.id);
    const next = dropIndexFromY(y, ids, rowNodesRef.current);
    dropIndexRef.current = next;
    setDropIndex(next);
  }, []);

  const bindRowRef = useCallback((id) => (node) => {
    if (node) rowNodesRef.current[id] = node;
    else delete rowNodesRef.current[id];
  }, []);

  const updateOverflow = useCallback((contentH, layoutH, offsetY) => {
    const metrics = scrollMetricsRef.current;
    if (contentH != null) metrics.contentH = contentH;
    if (layoutH != null) metrics.layoutH = layoutH;
    if (offsetY != null) metrics.offsetY = offsetY;
    const overflow = metrics.contentH > metrics.layoutH + 2;
    setCanScroll(overflow);
    setAtEnd(!overflow || metrics.offsetY + metrics.layoutH >= metrics.contentH - 8);
  }, []);

  if (!items.length) return null;

  const count = items.length;
  const title = workFollowUpQueueCountLabel(count);
  const rowHeight = isXS ? ROW_HEIGHT : ROW_HEIGHT_DESKTOP;
  const maxVisible = isXS ? MAX_VISIBLE_MOBILE : MAX_VISIBLE_DESKTOP;
  const listMaxHeight = Math.min(count, maxVisible) * rowHeight;
  const showFade = count > maxVisible && canScroll && !atEnd;

  return (
    <View
      style={[
        styles.card,
        isXS ? styles.cardCompact : styles.cardDesktop,
        {
          borderColor: colors.border_color_secondary,
          backgroundColor: colors.solid_color_block,
        },
      ]}
      accessibilityRole="none"
      accessibilityLabel={title}
      accessibilityHint="Drag to reorder, or use move up and down"
    >
      <View style={styles.header}>
        <View style={styles.headerTitle}>
          <View style={[styles.liveDot, { backgroundColor: isActive ? colors.color_success : colors.text_color_tertiary }]} />
          <Text size={isXS ? 's' : 'n'} weight="semibold" numberOfLines={1}>
            {title}
          </Text>
        </View>
        {count > 1 ? (
          <Pressable
            onPress={onClear}
            accessibilityRole="button"
            accessibilityLabel="Clear queued follow-ups"
            style={({ hovered }) => [styles.clearBtn, hovered && { opacity: 0.75 }]}
          >
            <Text size="xs" color="secondary">Clear</Text>
          </Pressable>
        ) : null}
      </View>
      <View style={styles.listWrap}>
        <ScrollView
          style={{ maxHeight: listMaxHeight }}
          nestedScrollEnabled
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={count > maxVisible}
          onLayout={(event) => updateOverflow(null, event.nativeEvent.layout.height, null)}
          onContentSizeChange={(_w, height) => updateOverflow(height, null, null)}
          onScroll={(event) => {
            const { contentOffset, contentSize, layoutMeasurement } = event.nativeEvent;
            updateOverflow(contentSize.height, layoutMeasurement.height, contentOffset.y);
          }}
          scrollEventThrottle={16}
        >
          {namedItems.map(({ item, fileNames }, index) => (
            <FollowUpRow
              key={item.id}
              item={item}
              index={index}
              count={count}
              fileNames={fileNames}
              sendingId={sendingId}
              compact={isXS}
              dragging={draggingId === item.id}
              dropTarget={draggingId && dropIndex === index && draggingId !== item.id}
              onEdit={onEdit}
              onDelete={onDelete}
              onSendNow={sendNow}
              onMove={moveTo}
              onDragStart={startDrag}
              onDragMove={moveDrag}
              onDragEnd={finishDrag}
              bindRowRef={bindRowRef(item.id)}
            />
          ))}
        </ScrollView>
        {showFade ? (
          <LinearGradient
            pointerEvents="none"
            colors={['transparent', colors.solid_color_block]}
            style={styles.fade}
          />
        ) : null}
      </View>
    </View>
  );
}

WorkFollowUpQueue.propTypes = {
  isActive: PropTypes.bool,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    text: PropTypes.string,
    fileIds: PropTypes.arrayOf(PropTypes.string),
  })),
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  onReorder: PropTypes.func,
  onSendNow: PropTypes.func,
  onClear: PropTypes.func,
};

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 6,
    gap: 4,
    marginTop: 10,
    marginBottom: 6,
  },
  cardCompact: {
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingTop: 6,
    paddingBottom: 4,
    gap: 3,
    marginTop: 8,
    marginBottom: 4,
  },
  cardDesktop: {
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingTop: 8,
    paddingBottom: 6,
    gap: 4,
    marginTop: 10,
    marginBottom: 6,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    minHeight: 22,
  },
  headerTitle: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    flex: 1,
  },
  liveDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
  },
  clearBtn: {
    paddingVertical: 2,
    paddingHorizontal: 2,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  listWrap: {
    position: 'relative',
  },
  fade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: 28,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 2,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rowCompact: {
    minHeight: ROW_HEIGHT,
    height: ROW_HEIGHT,
  },
  rowDesktop: {
    minHeight: ROW_HEIGHT_DESKTOP,
    height: ROW_HEIGHT_DESKTOP,
  },
  grip: {
    width: 22,
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'grab' } : {}),
  },
  moveCol: {
    width: 18,
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  moveBtn: {
    width: 18,
    height: 14,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 4,
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  itemCopy: {
    flex: 1,
    minWidth: 0,
    justifyContent: 'center',
  },
  itemActions: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 4,
  },
  iconBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  sendNowChip: {
    height: 24,
    paddingHorizontal: 8,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  editRow: {
    paddingHorizontal: 8,
    paddingVertical: 8,
    gap: 8,
    borderWidth: 1,
    borderRadius: 10,
    marginVertical: 2,
  },
  editInput: {
    minHeight: 44,
    fontSize: 14,
    lineHeight: 20,
    padding: 0,
    ...(Platform.OS === 'web' ? { outlineStyle: 'none', borderWidth: 0 } : {}),
  },
  textBtn: {
    height: 28,
    paddingHorizontal: 8,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
  saveBtn: {
    height: 28,
    paddingHorizontal: 12,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    ...(Platform.OS === 'web' ? { cursor: 'pointer' } : {}),
  },
});
